import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container } from './styles';
import { FiDownload, FiPlus, FiSearch, FiMonitor, FiClock, FiX, FiLock, FiCalendar, FiActivity, FiZap, FiCpu, FiServer, FiTerminal, FiArrowLeft, FiAlertTriangle, FiMove } from 'react-icons/fi';
import { GoFile, GoCheck, GoSync, GoX, GoAlert } from 'react-icons/go';
import { BiSpreadsheet } from 'react-icons/bi';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import NewRequestModal from './components/NewRequestModal';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const STATUS_LABELS = {
  pending: 'Pendente',
  processing: 'Baixando',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  error: 'Erro'
};

function fmtNum(n) {
  if (!n && n !== 0) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

function SortableRequestCard({ id, children, isDraggable }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !isDraggable,
    transition: {
      duration: 200,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 100 : 'auto',
    position: 'relative',
    height: '100%',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-wrapper ${isDragging ? 'is-dragging' : ''} ${isDraggable ? 'is-draggable' : ''}`}
      {...(isDraggable ? { ...attributes, ...listeners } : {})}
    >
      {children}
    </div>
  );
}

export default function NfeDownloader() {
  const { user } = useAuth();
  const isGod = user?.cargo === 'god';
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [monitor, setMonitor] = useState(null);
  const [monitorTab, setMonitorTab] = useState('server'); // 'server' | 'nodes'
  const [logs, setLogs] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null); // nodeId or null
  const [showNewModal, setShowNewModal] = useState(false);
  const terminalRef = useRef(null);
  const nodeTerminalRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDragId, setActiveDragId] = useState(null);
  const [dragWidth, setDragWidth] = useState(null);
  const wsRef = useRef(null);
  const [nowTick, setNowTick] = useState(Date.now());

  // Fetch ALL requests (admin view)
  const fetchRequests = useCallback(async () => {
    if (!isGod) { setLoading(false); return; }
    try {
      const response = await api.get('/nfe-downloader/requests', {
        params: { all: 'true' }
      });
      if (response.data.success) {
        setRequests(response.data.data || []);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching requests:', error);
      }
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [isGod]);

  // Fetch stats (all projects)
  const fetchStats = useCallback(async () => {
    if (!isGod) return;
    try {
      const response = await api.get('/nfe-downloader/stats', {
        params: { all: 'true' }
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      // Stats may not exist yet
    }
  }, [isGod]);

  // Setup WebSocket for real-time updates
  useEffect(() => {
    if (!isGod) return;
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    if (!token) return;

    const wsUrl = process.env.REACT_APP_WS_URL || 'wss://log.vidal-app.com/ws';
    const ws = new WebSocket(`${wsUrl}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'nfe_download_monitor'
      }));
    };

    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        const data = parsed.message || parsed;

        if (data.type === 'nfe_download_monitor' && data.data) {
          setMonitor(data.data);
          setNodes(data.data.nodes || []);
          if (data.data.logs) {
            setLogs(data.data.logs);
            // Auto-scroll terminals
            setTimeout(() => {
              if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
              if (nodeTerminalRef.current) nodeTerminalRef.current.scrollTop = nodeTerminalRef.current.scrollHeight;
            }, 50);
          }
        }

        if (data.type === 'nfe_download_progress' && data.data) {
          handleProgressUpdate(data.data);
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onerror = () => { /* silent */ };
    ws.onclose = () => { /* will reconnect on remount */ };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [isGod]);

  // Handle real-time progress updates
  const handleProgressUpdate = useCallback((progressData) => {
    setRequests(prev => prev.map(req => {
      if (req.requestId === progressData.requestId) {
        return {
          ...req,
          status: progressData.status || req.status,
          firstDownloadAt: req.firstDownloadAt || progressData.firstDownloadAt || null,
          lastDownloadAt: progressData.lastDownloadAt || req.lastDownloadAt || null,
          chaves: {
            ...req.chaves,
            baixadas: progressData.downloaded ?? req.chaves.baixadas,
            erros: progressData.errors ?? req.chaves.erros,
            pendentes: req.chaves.total - (progressData.downloaded ?? req.chaves.baixadas) - (progressData.errors ?? req.chaves.erros)
          }
        };
      }
      return req;
    }));
  }, []);

  // 1s ticker so "último download" elapsed time stays live
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Initial load
  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests, fetchStats]);

  // Cancel request
  const handleCancel = async (e, requestId) => {
    e.stopPropagation();
    try {
      await api.delete(`/nfe-downloader/requests/${requestId}`);
      toast.success('Requisição cancelada');
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao cancelar requisição');
    }
  };

  // Download ZIP — native browser download (background, no new tab)
  const handleDownloadZip = (requestId) => {
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    if (!token) { toast.error('Sessão expirada'); return; }
    const baseURL = process.env.REACT_APP_API || 'https://api.vidal-app.com';
    const url = `${baseURL}/nfe-downloader/requests/${requestId}/download-zip?token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Calculate progress percentage
  const getProgress = (req) => {
    if (!req.chaves || req.chaves.total === 0) return 0;
    return Math.round(((req.chaves.baixadas + req.chaves.erros) / req.chaves.total) * 100);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // KPI totals
  const totalKeys = requests.reduce((sum, r) => sum + (r.chaves?.total || 0), 0);
  const totalDownloaded = requests.reduce((sum, r) => sum + (r.chaves?.baixadas || 0), 0);
  const totalErrors = requests.reduce((sum, r) => sum + (r.chaves?.erros || 0), 0);
  const activeRequestCount = requests.filter(r => r.status === 'processing' || r.status === 'pending').length;

  const lastRequest = requests.length > 0
    ? formatDate(requests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0].createdAt)
    : '-';

  // Filter
  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (req.companyName || '').toLowerCase().includes(term) ||
      (req.cnpj || '').includes(term) ||
      (req.requestId || '').toLowerCase().includes(term) ||
      (req.projeto || '').toLowerCase().includes(term)
    );
  });

  // Sort: active (pending/processing) by priority first, then completed/cancelled
  const sortedRequests = useMemo(() => {
    const active = filteredRequests
      .filter(r => r.status === 'pending' || r.status === 'processing')
      .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999) || new Date(a.createdAt) - new Date(b.createdAt));
    const done = filteredRequests
      .filter(r => r.status !== 'pending' && r.status !== 'processing')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return [...active, ...done];
  }, [filteredRequests]);

  const sortedIds = useMemo(() => sortedRequests.map(r => r.requestId), [sortedRequests]);

  const activeRequests = useMemo(() =>
    sortedRequests.filter(r => r.status === 'pending' || r.status === 'processing'),
  [sortedRequests]);

  // Current list being processed (top of priority queue)
  const currentRequest = useMemo(() =>
    activeRequests.find(r => r.status === 'processing') || activeRequests[0] || null,
  [activeRequests]);

  // Speed of the CURRENT list = recent pace (2-min window) reported by the WS, sums all nodes.
  const currentListNps = useMemo(() => {
    if (!currentRequest) return null;
    const r = monitor?.rates?.byRequest?.[currentRequest.requestId];
    return r ? r.perSecond : null;
  }, [currentRequest, monitor]);

  // DnD — Notion-style
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback((event) => {
    setActiveDragId(event.active.id);
    const el = event.active.node?.current;
    if (el) setDragWidth(el.getBoundingClientRect().width);
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedIds.indexOf(active.id);
    const newIndex = sortedIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Only allow reorder within active requests
    const draggable = sortedRequests.filter(r => r.status === 'pending' || r.status === 'processing');
    const activeIds = draggable.map(r => r.requestId);
    if (!activeIds.includes(active.id) || !activeIds.includes(over.id)) return;

    const oldActiveIdx = activeIds.indexOf(active.id);
    const newActiveIdx = activeIds.indexOf(over.id);
    const reordered = arrayMove(activeIds, oldActiveIdx, newActiveIdx);

    // Optimistic update: assign new priorities locally
    setRequests(prev => {
      const updated = [...prev];
      for (let i = 0; i < reordered.length; i++) {
        const r = updated.find(u => u.requestId === reordered[i]);
        if (r) r.priority = i;
      }
      return updated;
    });

    // Persist to backend (single batch request)
    try {
      await api.put('/nfe-downloader/requests/reorder', {
        order: reordered.map((reqId, idx) => ({ requestId: reqId, priority: idx }))
      });
    } catch (err) {
      console.error('Error updating priorities:', err);
      toast.error('Erro ao salvar prioridades');
      fetchRequests();
    }
  }, [sortedIds, sortedRequests, fetchRequests]);

  if (!isGod) {
    return (
      <Container>
        <div className="empty-state">
          <div className="empty-icon"><FiLock /></div>
          <div className="empty-title">Acesso restrito</div>
          <div className="empty-description">
            Esta tela é exclusiva para administradores.
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-icon"><FiDownload /></div>
        <div className="header-details">
          <h3>NFe Downloader</h3>
          <div className="header-meta">
            <span className="status-tag active">{activeRequestCount > 0 ? `${activeRequestCount} ativa(s)` : 'Nenhuma ativa'}</span>
            <span className="update-tag">
              {requests.length > 0 ? `Última requisição: ${lastRequest}` : 'Aguardando primeira requisição'}
            </span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      {loading ? (
        <div className="kpi-cards">
          <div className="kpi-card primary">
            <div className="card-content">
              <div className="thumb"><GoSync className="spin" /></div>
              <div className="text">
                <span className="kpi-label">Carregando...</span>
                <span className="kpi-value">-</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="kpi-cards">
          <div className="kpi-card primary">
            <div className="card-content">
              <div className="thumb"><GoFile /></div>
              <div className="text">
                <span className="kpi-label">Total Chaves</span>
                <span className="kpi-value">{totalKeys.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="kpi-card success with-progress">
            <div className="card-content">
              <div className="thumb"><GoCheck /></div>
              <div className="text">
                <span className="kpi-label">Baixadas</span>
                <span className="kpi-value">{totalDownloaded.toLocaleString()}</span>
              </div>
            </div>
            <div className="kpi-progress">
              <div className="kpi-progress-fill" style={{ width: `${totalKeys > 0 ? (totalDownloaded / totalKeys) * 100 : 0}%` }} />
            </div>
          </div>

          <div className="kpi-card error with-progress">
            <div className="card-content">
              <div className="thumb"><GoX /></div>
              <div className="text">
                <span className="kpi-label">Erros</span>
                <span className="kpi-value">{totalErrors.toLocaleString()}</span>
              </div>
            </div>
            <div className="kpi-progress">
              <div className="kpi-progress-fill" style={{ width: `${totalKeys > 0 ? (totalErrors / totalKeys) * 100 : 0}%` }} />
            </div>
          </div>

          <div className="kpi-card warning">
            <div className="card-content">
              <div className="thumb"><FiMonitor /></div>
              <div className="text">
                <span className="kpi-label">Nós Conectados</span>
                <span className="kpi-value">{nodes.length}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button className="toolbar-btn" onClick={() => setShowNewModal(true)}>
            <FiPlus />
            Nova Requisição
          </button>
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por empresa, CNPJ ou projeto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Main Content: Monitor Sidebar (LEFT) + Grid */}
      <div className="main-layout">
        {/* Monitor Sidebar — LEFT */}
        <div className="monitor-sidebar">
          {/* Tabs */}
          <div className="monitor-tabs">
            <button
              className={`monitor-tab ${monitorTab === 'server' ? 'active' : ''}`}
              onClick={() => setMonitorTab('server')}
            >
              <FiServer size={13} />
              Servidor
            </button>
            <button
              className={`monitor-tab ${monitorTab === 'nodes' ? 'active' : ''}`}
              onClick={() => setMonitorTab('nodes')}
            >
              <FiCpu size={13} />
              Nós ({nodes.length})
            </button>
          </div>

          {monitorTab === 'server' ? (
            <>
              {/* ═══ PROGRESSO ═══ */}
              <div className="monitor-card general">
                <div className="monitor-card-header">
                  <FiActivity size={14} />
                  <span>Progresso</span>
                  <div className={`live-dot ${monitor ? 'active' : ''}`} />
                </div>

                <div className="monitor-stats">
                  {/* Empresa ativa com thumb */}
                  {(() => {
                    const processing = requests.find(r => r.status === 'processing') || requests.find(r => r.status === 'pending') || requests[0];
                    if (!processing) return null;
                    const total = processing.chaves?.total || 0;
                    const baixadas = processing.chaves?.baixadas || 0;
                    const pct = total > 0 ? ((baixadas / total) * 100).toFixed(1) : '0';
                    const initial = (processing.companyName || 'E')[0].toUpperCase();
                    return (
                      <div className="current-company">
                        <div className="current-company-thumb">{initial}</div>
                        <div className="current-company-info">
                          <span className="current-company-name">{processing.companyName || processing.cnpj || '—'}</span>
                          <span className="current-company-cnpj">{processing.cnpj || '—'}</span>
                        </div>
                        <span className="current-company-pct">{pct}%</span>
                      </div>
                    );
                  })()}

                  {/* Tag último download */}
                  <div className="last-download-tag">
                    <FiClock size={10} />
                    <span>
                      {(() => {
                        let lastTs = monitor?.rates?.lastCompletionTs || 0;
                        if (!lastTs) {
                          for (const n of nodes) {
                            const ts = n.lastCompletedKey || (n.lastSeen ? new Date(n.lastSeen).getTime() : 0);
                            if (ts > lastTs) lastTs = ts;
                          }
                        }
                        if (!lastTs && monitor?.timestamp) {
                          lastTs = new Date(monitor.timestamp).getTime() || 0;
                        }
                        if (!lastTs) return 'Sem downloads';
                        const ago = Math.floor((Date.now() - lastTs) / 1000);
                        if (ago < 5) return 'Último: agora';
                        if (ago < 60) return `Último: ${ago}s atrás`;
                        if (ago < 3600) return `Último: ${Math.floor(ago / 60)}m atrás`;
                        return `Último: ${Math.floor(ago / 3600)}h atrás`;
                      })()}
                    </span>
                  </div>
                  {(() => {
                    const cur = currentRequest?.chaves || { total: 0, baixadas: 0, erros: 0 };
                    const curTotal = cur.total || 0;
                    const curBaixadas = cur.baixadas || 0;
                    const curErros = cur.erros || 0;
                    const curFaltantes = Math.max(0, curTotal - curBaixadas - curErros);
                    const curPct = curTotal > 0 ? (curBaixadas / curTotal) * 100 : 0;
                    return (
                      <>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Total de chaves</span>
                          <span className="stat-value accent">{curTotal.toLocaleString()}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Notas baixadas</span>
                          <span className="stat-value accent">{curBaixadas.toLocaleString()}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Baixadas na sessão</span>
                          <span className="stat-value">{(monitor?.rates?.totalSession ?? 0).toLocaleString()}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Notas faltantes</span>
                          <span className="stat-value error-text">{curFaltantes.toLocaleString()}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Erros</span>
                          <span className="stat-value error-text">{curErros.toLocaleString()}</span>
                        </div>
                        <div className="monitor-progress-bar" style={{ marginTop: 8 }}>
                          <div className="monitor-progress-fill" style={{ width: `${curPct}%` }} />
                        </div>
                        <div className="monitor-stat-row" style={{ marginTop: 4 }}>
                          <span className="stat-label">Progresso da lista</span>
                          <span className="stat-value accent">{`${curPct.toFixed(2)}%`}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* ═══ VELOCIDADE ═══ */}
              <div className="monitor-card general">
                <div className="monitor-card-header">
                  <FiZap size={14} />
                  <span>Velocidade (lista atual)</span>
                </div>
                <div className="monitor-stats">
                  {(() => {
                    const nps = currentListNps ?? (monitor?.rates?.perSecond10m ?? 0);
                    return (
                      <>
                        <div className="monitor-stat-row highlight">
                          <span className="stat-label">Por segundo</span>
                          <span className="stat-value accent">{nps.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} notas/s</span>
                        </div>
                        <div className="monitor-stat-row highlight">
                          <span className="stat-label">Por minuto</span>
                          <span className="stat-value accent">{(nps * 60).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} notas/min</span>
                        </div>
                        <div className="monitor-stat-row highlight">
                          <span className="stat-label">Por hora</span>
                          <span className="stat-value accent">{Math.round(nps * 3600).toLocaleString()} notas/h</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Por dia</span>
                          <span className="stat-value">{Math.round(nps * 86400).toLocaleString()} notas/dia</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Por mês</span>
                          <span className="stat-value">{Math.round(nps * 2592000).toLocaleString()} notas/mês</span>
                        </div>
                        <div className="monitor-stat-divider" />
                        <div className="monitor-stat-row">
                          <span className="stat-label">Tempo médio/XML</span>
                          <span className="stat-value">{(monitor?.rates?.avgDownloadTime10m || monitor?.rates?.avgDownloadTime) ? `${((monitor.rates.avgDownloadTime10m || monitor.rates.avgDownloadTime) / 1000).toFixed(1)}s` : '-'}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* ═══ TEMPO ═══ */}
              <div className="monitor-card general">
                <div className="monitor-card-header">
                  <FiClock size={14} />
                  <span>Tempo</span>
                </div>
                <div className="monitor-stats">
                  <div className="monitor-stat-row">
                    <span className="stat-label">Uptime</span>
                    <span className="stat-value accent">
                      {monitor?.rates?.uptime
                        ? (() => {
                            const s = monitor.rates.uptime;
                            const d = Math.floor(s / 86400);
                            const h = Math.floor((s % 86400) / 3600);
                            const m = Math.floor((s % 3600) / 60);
                            if (d > 0) return `${d}d ${h}h ${m}m`;
                            if (h > 0) return `${h}h ${m}m`;
                            return `${m}m ${s % 60}s`;
                          })()
                        : '-'}
                    </span>
                  </div>
                  <div className="monitor-stat-row">
                    <span className="stat-label">Último download</span>
                    <span className="stat-value">
                      {(() => {
                        const tsRaw = currentRequest?.lastDownloadAt || monitor?.rates?.lastCompletionTs || null;
                        if (!tsRaw) return '-';
                        const ts = typeof tsRaw === 'number' ? tsRaw : new Date(tsRaw).getTime();
                        let secs = Math.floor((nowTick - ts) / 1000);
                        if (secs < 0) secs = 0;
                        if (secs < 60) return `há ${secs}s`;
                        const d = Math.floor(secs / 86400);
                        const h = Math.floor((secs % 86400) / 3600);
                        const m = Math.floor((secs % 3600) / 60);
                        const s = secs % 60;
                        if (d > 0) return `há ${d}d ${h}h`;
                        if (h > 0) return `há ${h}h ${m}m`;
                        return `há ${m}m ${s}s`;
                      })()}
                    </span>
                  </div>
                  <div className="monitor-stat-row">
                    <span className="stat-label">Tempo restante</span>
                    <span className="stat-value">
                      {(() => {
                        const nps = currentListNps ?? (monitor?.rates?.perSecond10m ?? 0);
                        const remaining = currentRequest
                          ? (currentRequest.chaves?.total || 0) - (currentRequest.chaves?.baixadas || 0) - (currentRequest.chaves?.erros || 0)
                          : (totalKeys - totalDownloaded - totalErrors);
                        if (nps <= 0 || remaining <= 0) return 'Calculando...';
                        const secs = remaining / nps;
                        const d = Math.floor(secs / 86400);
                        const h = Math.floor((secs % 86400) / 3600);
                        const m = Math.floor((secs % 3600) / 60);
                        if (d > 0) return `${d}d ${h}h ${m}m`;
                        if (h > 0) return `${h}h ${m}m`;
                        return `${m}m`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* ═══ FILA DE PRIORIDADE ═══ */}
              {activeRequests.length > 0 && (
                <div className="monitor-card general">
                  <div className="monitor-card-header">
                    <FiServer size={14} />
                    <span>Fila ({activeRequests.length})</span>
                  </div>
                  <div className="priority-queue">
                    {activeRequests.map((req, idx) => {
                      const pct = req.chaves?.total > 0 ? Math.round((req.chaves.baixadas / req.chaves.total) * 100) : 0;
                      const uptime = monitor?.rates?.uptime || 0;
                      const session = monitor?.rates?.totalSession || 0;
                      const nps = uptime > 0 ? session / uptime : 0;
                      const remaining = (req.chaves?.total || 0) - (req.chaves?.baixadas || 0) - (req.chaves?.erros || 0);
                      let eta = '';
                      if (nps > 0 && remaining > 0) {
                        const secs = remaining / nps;
                        const d = Math.floor(secs / 86400);
                        const h = Math.floor((secs % 86400) / 3600);
                        const m = Math.floor((secs % 3600) / 60);
                        if (d > 0) eta = `~${d}d ${h}h`;
                        else if (h > 0) eta = `~${h}h ${m}m`;
                        else eta = `~${m}m`;
                      }
                      return (
                        <div key={req.requestId} className={`priority-item ${req.status}`}>
                          <span className="priority-pos">#{idx + 1}</span>
                          <div className="priority-info">
                            <span className="priority-name">{req.companyName || 'Empresa'}</span>
                            <span className="priority-cnpj">{req.cnpj}</span>
                          </div>
                          <div className="priority-nums">
                            <span className="priority-total">{fmtNum(req.chaves?.total || 0)}</span>
                            <span className="priority-done">{fmtNum(req.chaves?.baixadas || 0)}</span>
                            <span className="priority-pct">{pct}%</span>
                            {eta && <span className="priority-eta">{eta}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ═══ SERVIÇOS ═══ */}
              <div className="monitor-card general">
                <div className="monitor-card-footer" style={{ paddingTop: 8 }}>
                  <div className="service-indicators">
                    <div className="service-dot online" title="Backend" />
                    <span>API</span>
                    <div className="service-dot online" title="WebSocket" />
                    <span>WS</span>
                    <div className={`service-dot ${nodes.length > 0 ? 'online' : 'offline'}`} title="Nós" />
                    <span>Nós ({nodes.length})</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {selectedNode ? (() => {
                const nd = nodes.find(n => n.nodeId === selectedNode);
                if (!nd) {
                  setSelectedNode(null);
                  return null;
                }
                const shortName = nd.nodeId.replace(/^node-/, '').split('-').slice(0, 2).join('-');
                const isActive = nd.status === 'downloading';
                const fmtTime = (s) => {
                  if (!s) return '-';
                  if (s < 60) return `${s}s`;
                  if (s < 3600) return `${Math.floor(s/60)}m ${s%60}s`;
                  return `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m ${s%60}s`;
                };
                return (
                  <div className="node-detail">
                    <button className="node-detail-back" onClick={() => setSelectedNode(null)}>
                      <FiArrowLeft size={14} /> Voltar aos nós
                    </button>

                    {/* Status Card */}
                    <div className="monitor-card general">
                      <div className="monitor-card-header">
                        <FiCpu size={14} />
                        <span>{shortName}</span>
                        <div className={`live-dot ${isActive ? 'downloading' : nd.status === 'idle' ? 'idle' : 'active'}`} />
                      </div>
                      <div className="monitor-stats">
                        <div className="monitor-stat-row">
                          <span className="stat-label">Status</span>
                          <span className={`stat-value ${isActive ? 'accent' : ''}`}>
                            {isActive ? 'Baixando' : nd.status === 'idle' ? 'Aguardando' : 'Online'}
                          </span>
                        </div>
                        {isActive && nd.currentKey && (
                          <div className="monitor-stat-row">
                            <span className="stat-label">Chave atual</span>
                            <span className="stat-value muted" style={{ fontSize: '0.65em', wordBreak: 'break-all', maxWidth: 180, textAlign: 'right' }}>{nd.currentKey}</span>
                          </div>
                        )}
                        <div className="monitor-stat-row">
                          <span className="stat-label">Conectado</span>
                          <span className="stat-value">{new Date(nd.connectedAt).toLocaleString('pt-BR')}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Uptime</span>
                          <span className="stat-value accent">{fmtTime(nd.uptimeSeconds)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Progress Card */}
                    <div className="monitor-card general">
                      <div className="monitor-card-header">
                        <FiActivity size={14} />
                        <span>Progresso</span>
                      </div>
                      <div className="monitor-stats">
                        <div className="monitor-stat-row highlight">
                          <span className="stat-label"><GoCheck size={11} /> XMLs baixados</span>
                          <span className="stat-value accent">{(nd.downloadCount || 0).toLocaleString()}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Na sessão</span>
                          <span className="stat-value">{(nd.sessionDownloads || 0).toLocaleString()}</span>
                        </div>
                        <div className="monitor-stat-divider" />
                        <div className="monitor-stat-row">
                          <span className="stat-label">Erros totais</span>
                          <span className="stat-value error-text">{nd.sessionErrors || 0}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">CAPTCHA falhas</span>
                          <span className="stat-value error-text">{nd.sessionCaptchaErrors || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Speed Card */}
                    <div className="monitor-card general">
                      <div className="monitor-card-header">
                        <FiZap size={14} />
                        <span>Velocidade</span>
                      </div>
                      <div className="monitor-stats">
                        <div className="monitor-stat-row highlight">
                          <span className="stat-label"><FiZap size={11} /> Por segundo</span>
                          <span className="stat-value accent">{nd.rates?.perSecond ?? 0} /s</span>
                        </div>
                        <div className="monitor-stat-row highlight">
                          <span className="stat-label"><FiZap size={11} /> Por minuto</span>
                          <span className="stat-value accent">{nd.rates?.perMinute ?? 0} /min</span>
                        </div>
                        <div className="monitor-stat-row highlight">
                          <span className="stat-label"><FiZap size={11} /> Por hora</span>
                          <span className="stat-value accent">{(nd.rates?.perHour ?? 0).toLocaleString()} /h</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Por dia</span>
                          <span className="stat-value">{(nd.rates?.perDay ?? 0).toLocaleString()} /dia</span>
                        </div>
                      </div>
                    </div>

                    {/* Timing Card */}
                    <div className="monitor-card general">
                      <div className="monitor-card-header">
                        <FiClock size={14} />
                        <span>Tempo</span>
                      </div>
                      <div className="monitor-stats">
                        <div className="monitor-stat-row">
                          <span className="stat-label">Tempo médio/XML</span>
                          <span className="stat-value accent">{nd.avgDownloadTime ? `${(nd.avgDownloadTime / 1000).toFixed(1)}s` : '-'}</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Último download</span>
                          <span className="stat-value">{nd.lastDownloadTime ? `${(nd.lastDownloadTime / 1000).toFixed(1)}s` : '-'}</span>
                        </div>
                        <div className="monitor-stat-divider" />
                        <div className="monitor-stat-row">
                          <span className="stat-label">Últimos 1 min</span>
                          <span className="stat-value">{nd.rates?.recent1m ?? 0} XMLs</span>
                        </div>
                        <div className="monitor-stat-row">
                          <span className="stat-label">Últimos 5 min</span>
                          <span className="stat-value">{nd.rates?.recent5m ?? 0} XMLs</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })() : (
                <>
                  {/* Node Cards list */}
                  {nodes.length > 0 ? nodes.map(node => {
                    const shortName = node.nodeId.replace(/^node-/, '').split('-').slice(0, 2).join('-');
                    const isActive = node.status === 'downloading';
                    return (
                      <div
                        key={node.nodeId}
                        className={`monitor-card node-card-v2 clickable ${isActive ? 'active' : ''}`}
                        onClick={() => setSelectedNode(node.nodeId)}
                      >
                        <div className="monitor-card-header">
                          <FiCpu size={14} />
                          <span className="node-title">{shortName}</span>
                          <div className={`live-dot ${isActive ? 'downloading' : node.status === 'idle' ? 'idle' : 'active'}`} />
                        </div>

                        <div className="node-v2-body">
                          <div className="node-v2-status">
                            <span className={`node-status-badge ${node.status}`}>
                              {isActive ? 'Baixando' : node.status === 'idle' ? 'Aguardando' : 'Online'}
                            </span>
                            {isActive && node.currentKey && (
                              <span className="node-current-key" title={node.currentKey}>
                                ...{node.currentKey.slice(-12)}
                              </span>
                            )}
                          </div>

                          <div className="node-v2-metrics">
                            <div className="node-metric">
                              <GoCheck size={12} className="success" />
                              <span className="metric-value">{(node.downloadCount || 0).toLocaleString()}</span>
                              <span className="metric-label">baixados</span>
                            </div>
                            <div className="node-metric">
                              <FiZap size={12} className="accent" />
                              <span className="metric-value">{node.ratePerHour || 0}</span>
                              <span className="metric-label">/hora</span>
                            </div>
                            <div className="node-metric">
                              <FiClock size={12} className="muted" />
                              <span className="metric-value">{node.avgDownloadTime ? `${(node.avgDownloadTime / 1000).toFixed(1)}s` : '-'}</span>
                              <span className="metric-label">média</span>
                            </div>
                          </div>

                          <div className="node-v2-footer">
                            <span className="node-uptime">
                              <FiClock size={10} />
                              {node.uptimeSeconds
                                ? node.uptimeSeconds < 3600
                                  ? `${Math.floor(node.uptimeSeconds / 60)}m`
                                  : `${Math.floor(node.uptimeSeconds / 3600)}h ${Math.floor((node.uptimeSeconds % 3600) / 60)}m`
                                : '-'}
                            </span>
                            {(node.errorCount > 0 || node.captchaErrors > 0) && (
                              <span className="node-errors">
                                <GoX size={10} />
                                {node.errorCount} {node.captchaErrors > 0 && `(${node.captchaErrors} CAPTCHA)`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="monitor-card node-card-v2 empty">
                      <div className="monitor-card-header">
                        <FiCpu size={14} />
                        <span>Nós de Download</span>
                      </div>
                      <div className="node-v2-body">
                        <div className="node-empty-state">
                          <FiServer size={20} />
                          <span>Nenhum nó conectado</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Request Cards */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={sortedIds} strategy={rectSortingStrategy}>
            <div className={`downloads-grid ${activeDragId ? 'is-sorting' : ''}`}>
              {/* New Request Card */}
              <div className="download-card new-request-card" onClick={() => setShowNewModal(true)}>
                <div className="new-request-content">
                  <FiPlus size={24} />
                  <span>Nova Requisição</span>
                </div>
              </div>

              {sortedRequests.map((req, idx) => {
                const progress = getProgress(req);
                const canDownload = (req.chaves?.baixadas || 0) > 0;
                const percentBaixadas = req.chaves?.total > 0
                  ? Math.round((req.chaves.baixadas / req.chaves.total) * 100)
                  : 0;
                const isActive = req.status === 'pending' || req.status === 'processing';
                const activeIdx = isActive ? activeRequests.indexOf(req) : -1;

                return (
                  <SortableRequestCard key={req.requestId} id={req.requestId} isDraggable={isActive}>
                    <div
                      className={`download-card ${activeDragId === req.requestId ? 'drag-source' : ''}`}
                      onClick={() => !activeDragId && canDownload && handleDownloadZip(req.requestId)}
                      style={{ cursor: isActive ? 'grab' : canDownload ? 'pointer' : 'default' }}
                      title={isActive ? 'Arraste para reordenar prioridade' : canDownload ? 'Clique para baixar ZIP' : 'Nenhum XML disponível'}
                    >
                      <span className={`status-badge ${req.status}`}>
                        {STATUS_LABELS[req.status] || req.status}
                      </span>

                      {isActive && activeIdx === 0 && <div className="priority-dot" />}

                      {isActive && (
                        <button
                          className="cancel-btn-corner"
                          onClick={(e) => { e.stopPropagation(); handleCancel(e, req.requestId); }}
                          title="Cancelar requisição"
                        >
                          <FiX />
                        </button>
                      )}

                      <div className="download-header">
                        <div className={`download-icon ${req.status === 'processing' ? 'processing-icon' : req.status === 'pending' ? 'pending-icon' : ''}`}>
                          <FiDownload />
                        </div>
                        <div className="download-info">
                          <h5>{req.companyName || 'Empresa'}</h5>
                          <span className="cnpj">{req.cnpj}</span>
                          {req.projeto && <span className="project-tag">{req.projeto}</span>}
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="download-progress-bar">
                        <span className="download-progress-text">{progress}%</span>
                        <div
                          className={`download-progress-fill ${req.status}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>

                      {/* Meta items */}
                      <div className="download-meta">
                        <div className="meta-item">
                          <GoFile />
                          <span className="meta-value">{fmtNum(req.chaves?.total || 0)}</span>
                        </div>
                        <div className="meta-item success">
                          <GoCheck />
                          <span className="meta-value">{fmtNum(req.chaves?.baixadas || 0)}</span>
                        </div>
                        <div className="meta-item error">
                          <GoX />
                          <span className="meta-value">{fmtNum(req.chaves?.erros || 0)}</span>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="download-footer">
                        <span><FiClock /> {formatDate(req.createdAt)}</span>
                        <span className="percent-badge" style={{
                          color: percentBaixadas >= 90 ? '#10b981' : percentBaixadas >= 70 ? '#f59e0b' : '#ef4444'
                        }}>
                          {percentBaixadas}% baixadas
                        </span>
                      </div>
                    </div>
                  </SortableRequestCard>
                );
              })}
            </div>
          </SortableContext>

          <DragOverlay dropAnimation={{
            duration: 200,
            easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
          }}>
            {activeDragId ? (() => {
              const req = requests.find(r => r.requestId === activeDragId);
              if (!req) return null;
              const progress = getProgress(req);
              const percentBaixadas = req.chaves?.total > 0
                ? Math.round((req.chaves.baixadas / req.chaves.total) * 100)
                : 0;
              const activeIdx = activeRequests.findIndex(r => r.requestId === activeDragId);
              return (
                <div className="download-card drag-overlay-card" style={{ cursor: 'grabbing', width: dragWidth || 'auto' }}>
                  <span className={`status-badge ${req.status}`}>
                    {STATUS_LABELS[req.status] || req.status}
                  </span>
                  {activeIdx === 0 && <div className="priority-dot" />}
                  <div className="download-header">
                    <div className={`download-icon ${req.status === 'processing' ? 'processing-icon' : 'pending-icon'}`}>
                      <FiDownload />
                    </div>
                    <div className="download-info">
                      <h5>{req.companyName || 'Empresa'}</h5>
                      <span className="cnpj">{req.cnpj}</span>
                      {req.projeto && <span className="project-tag">{req.projeto}</span>}
                    </div>
                  </div>
                  <div className="download-progress-bar">
                    <span className="download-progress-text">{progress}%</span>
                    <div className={`download-progress-fill ${req.status}`} style={{ width: `${progress}%` }} />
                  </div>
                  <div className="download-meta">
                    <div className="meta-item"><GoFile /><span className="meta-value">{fmtNum(req.chaves?.total || 0)}</span></div>
                    <div className="meta-item success"><GoCheck /><span className="meta-value">{fmtNum(req.chaves?.baixadas || 0)}</span></div>
                    <div className="meta-item error"><GoX /><span className="meta-value">{fmtNum(req.chaves?.erros || 0)}</span></div>
                  </div>
                  <div className="download-footer">
                    <span><FiClock /> {formatDate(req.createdAt)}</span>
                    <span className="percent-badge" style={{
                      color: percentBaixadas >= 90 ? '#10b981' : percentBaixadas >= 70 ? '#f59e0b' : '#ef4444'
                    }}>{percentBaixadas}% baixadas</span>
                  </div>
                </div>
              );
            })() : null}
          </DragOverlay>
        </DndContext>

      </div>

      {/* New Request Modal */}
      <NewRequestModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={fetchRequests}
      />
    </Container>
  );
}
